import React from 'react'
import { routes } from '@sketch/modules-common'
import styled, { css } from 'styled-components'
import copy from 'copy-to-clipboard'
import { useToast } from '@sketch/toasts'
import {
  Box,
  Truncate,
  FlatButton,
  Link,
  Tooltip,
  useModalContext,
} from '@sketch/components'
import { useRefreshPreviewMutation } from '@sketch/gql-types'
import { usePreviewForm } from './usePreviewForm'
import { ModalConfirmation } from './ModalConfirmation'
import { ReactComponent as Open } from '@sketch/icons/arrow-right-circle-semifilled-17'
import { ReactComponent as Rectangles } from '@sketch/icons/rectangles-2-17'
import { ReactComponent as Refresh } from '@sketch/icons/arrow-counterclockwise-17'
import { ReactComponent as GlobeIcon } from '@sketch/icons/globe-64'

const PreviewBox = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.foreground.secondary.D};
    background-color: ${theme.colors.background.secondary.B};
    border: 1px dashed ${theme.colors.border.A};
    border-radius: ${theme.radii.large};
    padding: 8px 12px;
    margin-top: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  `
)

const iconStyles = css`
  width: 17px;
  height: 17px;
`

const OpenIcon = styled(Open)`
  ${iconStyles}
`
const CopyIcon = styled(Rectangles)`
  ${iconStyles}
`
const RefreshIcon = styled(Refresh)`
  ${iconStyles}
`

const ActionButton = styled(FlatButton)`
  ${iconStyles};
  padding: 0;
  margin-left: 12px;
`

const ActionLink = styled(Link)(
  ({ theme }) => css`
    color: ${theme.colors.foreground.secondary.D};
    padding: 0;
    margin-left: 12px;

    &:hover {
      color: ${theme.colors.foreground.secondary.D};
    }
  `
)

export const PreviewLinkSection = () => {
  const { showToast } = useToast()
  const { showModal, hideModal } = useModalContext()
  const { setPreviewIdentifier, previewIdentifier } = usePreviewForm()

  const [refreshURL, { loading }] = useRefreshPreviewMutation({
    onCompleted: ({ refreshPreview }) => {
      showToast('Preview URL refreshed successfully')
      setPreviewIdentifier(refreshPreview.preview.identifier)
    },
    onError: 'show-toast',
  })

  if (!previewIdentifier) {
    return <></>
  }

  const previewURL = routes.PREVIEW_VIEW.create({
    previewID: previewIdentifier || '',
  })

  const url = `${window.location.origin}${previewURL}`

  const handleOnCopy = () => {
    copy(url)
    showToast('Link copied')
  }

  const handleOnRefresh = () => {
    if (!previewIdentifier) {
      return null
    }

    showModal(ModalConfirmation, {
      title: 'Generate New Preview Link?',
      description:
        'Anyone with the existing link will no longer have access to this preview.',
      confirmText: 'Generate New Link',
      icon: <GlobeIcon />,
      onConfirm: () => {
        refreshURL({ variables: { identifier: previewIdentifier } })
        hideModal()
      },
      variant: 'primary',
      loading,
    })
  }

  return (
    <Box mb={4}>
      Preview Link
      <PreviewBox>
        <Box maxWidth={450}>
          <Truncate>{url}</Truncate>
        </Box>
        <>
          <Tooltip content="Generate new link" placement="bottom">
            <ActionButton onClick={handleOnRefresh}>
              <RefreshIcon />
            </ActionButton>
          </Tooltip>
          <Tooltip content="Copy link" placement="bottom">
            <ActionButton>
              <CopyIcon onClick={handleOnCopy} />
            </ActionButton>
          </Tooltip>
          <Tooltip content="Open Preview" placement="bottom">
            <ActionLink to={previewURL} target="_blank">
              <OpenIcon />
            </ActionLink>
          </Tooltip>
        </>
      </PreviewBox>
    </Box>
  )
}
